/**
 * Modelos del contrato `PAYMENTS_FRONT_CONTRATO.md` — Polipay Payments (`/api/v1/payments/`).
 * Cobros e-commerce (card-not-present): links de cobro + cargos directos con tarjeta cifrada.
 *
 * Convenciones del backend (mantenidas tal cual, sin conversión de casing):
 *  - Requests envían snake_case (`id_company`, `redirect_url`, `expires_at`, ...).
 *  - Responses mezclan: los links vienen en snake_case (`expires_at`, `created_at`, `id_company`),
 *    los charges vienen en camelCase (`netAmount`, `requires3ds`, `failureReason`, `redirectUrl`, `createdAt`).
 */

/** Hoy solo se ha probado MXN (contrato §9). */
export type Currency = 'MXN';

/**
 * Estado de un link de cobro. `Cancelled` lo devuelve `POST /links/{id}/cancel/`
 * (§Cambios 2026-07-21) y también aparece como filtro válido en `GET /links/`.
 * Semántica: el link ya no es un cobro vigente, pero la página de pago del gateway
 * puede seguir aceptando pago hasta `expires_at`; si eso ocurre, la fila queda
 * `Cancelled` + `paid_after_cancel: true` (alerta de devolución).
 */
export type PaymentLinkStatus = 'Pending' | 'Completed' | 'Expired' | 'Cancelled';

/**
 * Valores aceptados como `?status=` en `GET /links/`. Además de los estados
 * reales del contrato (`PaymentLinkStatus`), el backend acepta el pseudo-valor
 * `'Deleted'` como filtro para pedir solo los rows con `deleted_at != null`
 * (auditoría de soft-delete). `Deleted` NO aparece nunca como `status` en el
 * response — solo como valor de filtro. Ver §Cambios Payments 2026-07-21.
 */
export type PaymentLinkStatusFilter = PaymentLinkStatus | 'Deleted';

/**
 * Estados de cargo del contrato (§GET /charges/). El orden ES el del contrato y lo
 * usan el filtro de Cargos y el desglose del Resumen. Lo cobrado es SOLO `Succeeded`.
 *
 * - `Pending`: en proceso.
 * - `PreAuthorized`: retención viva, todavía sin capturar (⚠️ no es ingreso).
 * - `Succeeded`: cobrado.
 * - `Failed`: declinado (el gateway igual manda `fee`/`tax`/neto positivos, §Huecos 5).
 * - `Refunded`: devuelto. `Voided`: cancelado el mismo día. `Reversed`: reversado.
 *   Los tres regresan el dinero de una venta aprobada y **solo los avisa el webhook**
 *   (§Huecos 3); el front NO puede dispararlos hoy (§Huecos 1).
 *
 * Es el catálogo ÚNICO: nadie vuelve a escribir la unión a mano.
 */
export const CHARGE_STATUSES = [
  'Pending',
  'PreAuthorized',
  'Succeeded',
  'Failed',
  'Refunded',
  'Voided',
  'Reversed',
] as const;
export type ChargeStatus = (typeof CHARGE_STATUSES)[number];

/**
 * Origen de un movimiento en `GET /charges/`.
 * `CHARGE` = cargo directo con tarjeta cifrada; `LINK` = pago hecho desde un link;
 * `PREAUTH` = retención apartada, todavía sin capturar (⚠️ no es ingreso);
 * `QR` = pago de un cobro QR desde una wallet Polipay.
 */
export const CHARGE_KINDS = ['CHARGE', 'LINK', 'PREAUTH', 'QR'] as const;
export type ChargeKind = (typeof CHARGE_KINDS)[number];

/**
 * Identifica el tenant que cobra. Acepta el PK entero (org) o el UUID nativo del
 * tenant TPV. Hoy opcional para admins globales; obligatorio cuando el flag
 * `PAYMENTS_REQUIRE_TENANT` se prenda en backend — enviarlo SIEMPRE desde ya.
 */
export type IdCompany = number | string;

// ── Link de cobro ────────────────────────────────────────────────

export interface PaymentLinkCreateRequest {
  /** Monto en 2 decimales como string (p. ej. "500.00"). Obligatorio, > 0. */
  amount: string;
  /** Default MXN si se omite. */
  currency?: Currency;
  description?: string;
  /** ISO-8601 UTC. */
  expires_at?: string;
  id_company?: IdCompany;
}

export interface PaymentLinkResponse {
  id_link: number;
  /** UUID del link en el gateway. */
  id: string;
  /** URL a mandarle al cliente. */
  url: string;
  amount: string;
  currency: Currency;
  description?: string;
  status: PaymentLinkStatus;
  id_company?: IdCompany;
  /** El POST /links/ devuelve `expiresAt` (camelCase). */
  expiresAt?: string;
  /** El GET /links/ devuelve `expires_at` y `created_at` (snake_case). */
  expires_at?: string;
  created_at?: string;
  /**
   * Timestamp del pago exitoso (§Cambios Payments 2026-07-27). Solo viene
   * con valor cuando `status === 'Completed'`; en cualquier otro estado el
   * backend omite el campo. Snake_case en el GET /links/, igual que
   * `expires_at`/`created_at`.
   */
  paid_at?: string;
  /**
   * Timestamp del soft-delete (§Cambios Payments 2026-07-21). `null` (o
   * ausente) = la fila está visible en el listado por defecto; con valor
   * significa que solo aparece cuando se pide `?include_deleted=1`.
   */
  deleted_at?: string | null;
  /**
   * `true` = link cancelado que terminó pagado (§Cambios Payments 2026-07-21).
   * ⚠️ ALERTA de devolución: la página de pago del gateway sigue viva hasta
   * `expires_at` y un cliente puede pagar después de cancelar; el operador
   * debe ejecutar refund. NUNCA lo pintes como venta normal en la UI.
   */
  paid_after_cancel?: boolean;
  /**
   * Nombre del comercio al que pertenece el link (§Cambios Payments 2026-09-22 §3).
   * Solo viene en el GET /links/ — el POST no lo devuelve.
   */
  company_name?: string;
  /**
   * Quién creó el link (§Cambios Payments 2026-09-22 §3). Para links creados
   * por API key viene como `apikey:<nombre>`. Solo en GET /links/.
   */
  created_by_label?: string;
}

export interface PaymentLinkListParams {
  id_company?: IdCompany;
  /**
   * Filtro server-side por estado. Acepta los estados del contrato
   * (`Pending`/`Completed`/`Expired`/`Cancelled`) y adicionalmente el
   * pseudo-valor `Deleted` (§Cambios Payments 2026-07-21) que devuelve solo
   * los rows con `deleted_at != null`.
   */
  status?: PaymentLinkStatusFilter;
  /** 1–200; default backend = 50. */
  limit?: number;
  /**
   * Desplazamiento para paginar server-side (§Cambios Payments 2026-09-22 §3).
   * Sin cursor: el backend usa limit/offset clásico.
   */
  offset?: number;
  /**
   * Búsqueda case-insensitive (§Filtros del listado). El backend matchea
   * contra: descripción, id del link, paymentId, email de quien lo creó.
   * Distinto scope al filtro client-side por `comercio`/`link_de_pago`.
   */
  search?: string;
  /**
   * Rango de fecha de creación (inclusive). Acepta fecha (`YYYY-MM-DD`) o
   * datetime ISO. Con fecha sola, `date_to` cubre el día completo.
   */
  date_from?: string;
  date_to?: string;
  /** Rango de monto inclusivo. Decimales aceptados (p. ej. `999.99`). */
  min_amount?: string;
  max_amount?: string;
  /**
   * `true` → añade `?include_deleted=1` al listado (§Cambios Payments
   * 2026-07-21). Devuelve TAMBIÉN los links soft-deleted (`deleted_at != null`)
   * junto con los visibles — es una vista de auditoría. La pantalla que sólo
   * quiere eliminados debe filtrar client-side por `deleted_at`.
   * `false`/ausente = comportamiento por defecto (solo visibles).
   */
  include_deleted?: boolean;
}

// ── Envío del link al cliente (§POST /links/{id_link}/send/) ────

export type PaymentLinkSendChannel = 'email' | 'sms';

export interface PaymentLinkSendRequest {
  channel: PaymentLinkSendChannel;
  /**
   * Para `email`: correo del destinatario. Para `sms`: 10 dígitos MX (backend
   * antepone `+52`) o E.164 (`+52...`); separadores comunes se toleran.
   */
  destination: string;
}

export interface PaymentLinkSendResponse {
  detail: string;
  channel: PaymentLinkSendChannel;
  destination: string;
}

export interface PaymentLinkListResponse {
  count: number;
  results: PaymentLinkResponse[];
}

// ── Summary por tenant ────────────────────────────────────────────

/** Bloque agregado por (moneda × estado). */
export interface SummaryBucket {
  count: number;
  amount: string;
  /**
   * Solo SIGNIFICA algo en `payments.<currency>.Succeeded` (contrato §Números del tenant).
   * Otros renglones pueden traerlo —`Failed` lo trae positivo, §Huecos 5— y NO se suma:
   * toda cifra de cobro sale de `breakdownPayments` (domain/models/payments-summary).
   * Puede traer hasta 6 decimales (§Huecos 6) y venir `null`/ausente: eso se pinta "—",
   * nunca `0.00`.
   */
  net_amount?: string | null;
}

export interface PaymentSummaryResponse {
  id_company?: IdCompany;
  /** `polipay-mirror` = espejo local mantenido por webhook + reconciliación 15min. */
  source: string;
  links: Partial<Record<Currency, Partial<Record<PaymentLinkStatus, SummaryBucket>>>>;
  payments: Partial<Record<Currency, Partial<Record<ChargeStatus, SummaryBucket>>>>;
}

/**
 * Desglose por comercio dentro de `PaymentSummaryGlobalResponse`.
 * Contrato §6 rev. 3 (2026-09-23).
 */
export interface PaymentCompanySummary {
  id_company: IdCompany;
  company_name: string;
  links: Partial<Record<Currency, Partial<Record<PaymentLinkStatus, SummaryBucket>>>>;
  payments: Partial<Record<Currency, Partial<Record<ChargeStatus, SummaryBucket>>>>;
}

/**
 * Respuesta de `GET /summary/` SIN `id_company` — totales globales de la empresa.
 * Contrato §6 rev. 3 (2026-09-23). Estructura distinta a `PaymentSummaryResponse`
 * (que se devuelve cuando se llama CON `id_company`).
 */
export interface PaymentSummaryGlobalResponse {
  source: string;
  total: {
    links: Partial<Record<Currency, Partial<Record<PaymentLinkStatus, SummaryBucket>>>>;
    payments: Partial<Record<Currency, Partial<Record<ChargeStatus, SummaryBucket>>>>;
  };
  por_comercio: PaymentCompanySummary[];
}

/**
 * Parámetros de `GET /api/v1/payments/summary/` (§Summary/Filtros).
 * El contrato acepta más filtros (`min_amount`, `max_amount`) que por ahora no
 * se implementan; `kind` y el rango `date_from`/`date_to` sí.
 *
 * ⚠️ Regla dura del contrato: `kind` acota SOLO la sección `payments`.
 * La sección `links` NO se filtra (cuenta links emitidos, otra dimensión).
 * Filtrar las tarjetas con el mismo `kind` que la tabla es obligatorio —
 * si no, la pantalla muestra dos números distintos del mismo dinero.
 */
export interface PaymentSummaryQueryParams {
  /** Acota un comercio específico. Sin él, respuesta global. */
  id_company?: IdCompany;
  /**
   * Acota SOLO la sección `payments`. La de `links` no se filtra.
   * `undefined` = default del backend (excluye PREAUTH). `'PREAUTH'`
   * explícito = "cuánto tengo retenido" (el default lo excluye, el filtro
   * explícito lo incluye). Un valor inválido devuelve 400.
   */
  kind?: ChargeKind;
  /**
   * Rango (`YYYY-MM-DD`, inclusivo) que el back aplica a la sección `links`. Lo usa el
   * dashboard de pagos con el rango YA resuelto por `/dashboard/` (mismo periodo).
   */
  date_from?: string;
  date_to?: string;
}

// ── Cargo directo (tarjeta cifrada) ───────────────────────────────

/**
 * Datos crudos de tarjeta. NUNCA se envían al backend. Solo se usan como input
 * de `PolipayPaymentsEncryptionService.encryptCard(card)` en el navegador.
 */
export interface CardData {
  cardNumber: string;
  cvv: string;
  expMonth: string;
  expYear: string;
  holderName: string;
}

/**
 * Blob cifrado que reemplaza a los datos de tarjeta en el body de `POST /charges/`.
 * `encryptedKey`: AES key envuelta con RSA-OAEP (base64).
 * `payload`: IV(12) + ciphertext AES-GCM (que incluye el tag GCM al final), base64.
 */
export interface EncryptedCard {
  encryptedKey: string;
  payload: string;
}

/** Dirección de facturación. Los campos son fijos; extras se descartan. */
export interface Billing {
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface PaymentChargeCreateRequest {
  /** Monto en 2 decimales. Obligatorio. */
  amount: string;
  currency?: Currency;
  /** URL a la que el gateway regresa al cliente tras 3DS. Obligatorio. */
  redirect_url: string;
  description?: string;
  customer_email?: string;
  id_company?: IdCompany;
  /** Obligatorio: NUNCA mandar `card`/`cardNumber`/`cvv` en claro (PCI → 400). */
  encrypted_card: EncryptedCard;
  billing?: Billing;
}

export interface PaymentChargeResponse {
  /** UUID del pago en el gateway. Se usa para `verify-3ds/`. */
  id: string;
  /** UUID local de la transacción en la suite. */
  id_transaction: string;
  /**
   * Ojo: contrato §Huecos 5 — los cargos declinados devuelven `fee`/`tax`/`netAmount`
   * positivos aunque `status: 'Failed'`. Filtrar por `status === 'Succeeded'` antes de sumar.
   * Ojo: contrato §Huecos 6 — `netAmount`/`fee`/`tax` pueden traer hasta 6 decimales.
   */
  amount: number;
  fee: number;
  tax: number;
  netAmount: number;
  currency: Currency;
  status: ChargeStatus;
  failureReason: string | null;
  requires3ds: boolean;
  /** Si `requires3ds: true`, redirigir al cliente aquí; al volver llamar a verify-3ds/. */
  redirectUrl: string | null;
  createdAt: string;
}

// ── Listado de cargos (§GET /charges/) ────────────────────────────

/** Filtros de `GET /api/v1/payments/charges/`. Se combinan entre sí. */
export interface PaymentChargeListParams {
  /**
   * OBLIGATORIO en la práctica para un operador acotado: sin él el backend
   * responde **403**, no lista vacía (mismo criterio que `/links/`).
   */
  id_company?: IdCompany;
  /** Correo del cliente. El backend NO distingue mayúsculas. */
  customer_email?: string;
  /** 1–200. */
  limit?: number;
  /**
   * Filtro por tipo de cargo. `CHARGE | LINK | PREAUTH`. No distingue mayúsculas.
   * Un valor inválido devuelve 400 con los valores aceptados.
   */
  kind?: ChargeKind;
  /**
   * Filtro por estado del cargo. Solo acepta un valor a la vez.
   */
  status?: ChargeStatus;
  /**
   * Fecha de inicio del rango (inclusive). Formato `YYYY-MM-DD`.
   * Contrato §2 (2026-09-22).
   */
  date_from?: string;
  /**
   * Fecha de fin del rango (inclusive). Formato `YYYY-MM-DD`.
   * Contrato §2 (2026-09-22).
   */
  date_to?: string;
  /**
   * Monto mínimo inclusivo. Decimales aceptados (p. ej. `100.00`).
   * Contrato §2 (2026-09-22).
   */
  min_amount?: string;
  /**
   * Monto máximo inclusivo. Decimales aceptados (p. ej. `999.99`).
   * Contrato §2 (2026-09-22).
   */
  max_amount?: string;
  /**
   * Búsqueda case-insensitive. El backend matchea contra: id, correo del cliente,
   * nombre del comercio. Contrato §2 (2026-09-22).
   */
  search?: string;
  /**
   * Desplazamiento para paginar server-side. Sin cursor: limit/offset clásico.
   * Contrato §2 (2026-09-22).
   */
  offset?: number;
}

/**
 * Renglón CRUDO de `GET /charges/`, antes de normalizar.
 *
 * Declara las DOS convenciones de nombre a propósito: el backend confirmó snake_case
 * en §2 (2026-09-22), pero como `POST /charges/` responde camelCase se mantienen
 * ambas formas — si el casing cambia, la tabla sale con celdas VACÍAS sin ningún
 * error observable. Los campos nuevos de §2 son todos snake_case.
 */
export interface PaymentChargeListRowDto {
  id?: string;
  id_transaction?: string;
  idTransaction?: string;
  /**
   * Origen del movimiento (§2 2026-09-22). `string` y no `ChargeKind`: es lo que de verdad
   * puede llegar; el mapper lo normaliza contra `CHARGE_KINDS`.
   */
  kind?: string | null;
  amount?: number | string;
  /** Comisión del gateway. Hasta 6 decimales; no truncar a 2. */
  fee?: number | string | null;
  /** IVA sobre la comisión. Hasta 6 decimales. */
  tax?: number | string | null;
  /** Neto liquidado al comercio (`amount - fee - tax`). Hasta 6 decimales. */
  net_amount?: number | string | null;
  netAmount?: number | string | null;
  currency?: Currency;
  /** Igual que `kind`: crudo; el mapper lo normaliza contra `CHARGE_STATUSES`. */
  status?: string | null;
  customer_email?: string | null;
  customerEmail?: string | null;
  /** Descripción libre del cargo o del link que lo originó. */
  description?: string | null;
  failure_reason?: string | null;
  failureReason?: string | null;
  id_company?: IdCompany | null;
  idCompany?: IdCompany | null;
  /** Nombre del comercio (§2 2026-09-22, mismo campo que en `/links/`). */
  company_name?: string | null;
  companyName?: string | null;
  created_at?: string;
  createdAt?: string;
  /** Solo filas `QR`: quién pagó, YA enmascarado por el back (`"Juan P."`). `null` en las demás. */
  payer_display_name?: string | null;
  /** Solo filas `QR`: desde qué app se pagó (`"LIBERATE"`). Es DATO, no se traduce. */
  wallet?: string | null;
}

/** Renglón ya normalizado que consume la pantalla. */
export interface PaymentChargeListRow {
  id: string;
  idTransaction: string;
  /** Origen: cargo directo (`CHARGE`) o pago desde link (`LINK`). */
  kind: ChargeKind | '';
  amount: number;
  /**
   * Hasta 6 decimales — no truncar al formatear. `null` cuando el gateway aún
   * no desglosó el cargo (contrato §2 2026-09-22); NO pintar como `$0.00`.
   */
  fee: number | null;
  tax: number | null;
  netAmount: number | null;
  currency: Currency;
  status: ChargeStatus | '';
  /** Vacío cuando el cargo no trae cliente (los pagos por link nunca lo traen). */
  customerEmail: string;
  description: string;
  /**
   * Valor crudo del backend. NUNCA se pinta tal cual (CLAUDE.md §Security): la
   * pantalla lo traduce a una clave i18n y cae a un genérico si no lo reconoce.
   */
  failureReason: string;
  idCompany: IdCompany | null;
  companyName: string;
  createdAt: string;
  /** Filas `QR`: pagador ya enmascarado. `''` en las demás. */
  payerDisplayName: string;
  /** Filas `QR`: wallet con que se pagó. `''` en las demás. */
  wallet: string;
}

export interface PaymentChargeListResponse {
  count: number;
  results: PaymentChargeListRow[];
}

// ── Encryption key ────────────────────────────────────────────────

export interface EncryptionKeyResponse {
  /**
   * PEM SPKI (`-----BEGIN PUBLIC KEY-----`, NO `RSA PUBLIC KEY`).
   * Cachéar en cliente: cambia raramente.
   */
  publicKey: string;
}

// ── Comercios de Payments (§4 sub-sección — GET /companies/) ──────

/**
 * Valor del campo `cobra_a` (§4 2026-09-22).
 * - `'cuenta propia'`  → el comercio tiene su propia cuenta en la pasarela (OK).
 * - `'SIN CONFIGURAR'` → no tiene destino; cualquier cobro produce 400 `PAYMENTS_MERCHANT_NOT_CONFIGURED`.
 * - Cualquier otro string → cobra a través de ese comercio configurado.
 */
export type CobraA = 'cuenta propia' | 'SIN CONFIGURAR' | string;

/**
 * Comercio del ecosistema de Payments devuelto por `GET /api/v1/payments/companies/`.
 * Para un rol interno global devuelve todos; para un operador solo sus comercios.
 */
export interface PaymentCompany {
  /**
   * ⚠️ El `id` INTERNO de Payments (pese al nombre heredado). Sirve para pintar y
   * para identificar la fila; NINGUNA ruta lo acepta como `id_company` (403/404).
   * Lo que viaja es `resolvePaymentsCompanyId` de
   * `domain/models/payments-companies/payments-company-id.ts`.
   */
  id_company: IdCompany;
  company_name: string;
  /**
   * Razón social (denominación legal). Opcional — puede venir del contrato
   * §Companies rev. 2026-09-25. Si el backend no la trae, `undefined`.
   */
  legal_name?: string;
  /**
   * PK de la empresa de banca en el core. `null` = comercio sin banca (uso interno).
   * Contrato §Companies rev. 2026-09-25.
   */
  org_company_id?: IdCompany | null;
  /**
   * Handle UUID externo del comercio (uso interno).
   * Contrato §Companies rev. 2026-09-25.
   */
  external_ref?: string;
  /**
   * A dónde va el dinero cobrado. `'SIN CONFIGURAR'` es la alerta de ops:
   * el comercio existe pero cualquier intento de cobro producirá 400.
   */
  cobra_a: CobraA;
  /**
   * ID del comercio en la pasarela. `null` si `cobra_a === 'SIN CONFIGURAR'`.
   */
  gateway_account_id: string | null;
  /** `false` = dado de baja; sus llaves dejan de servir. */
  is_active?: boolean;
}

export interface PaymentCompanyListResponse {
  count: number;
  results: PaymentCompany[];
}

/**
 * DTO crudo de `GET /api/v1/payments/companies/`.
 *
 * El backend devuelve `id` y `name` (no `id_company`/`company_name`).
 * Se mantienen los aliases camelCase como fallback defensivo.
 */
export interface PaymentCompanyDto {
  /** PK en la plataforma de Payments (campo real del API: `id`). */
  id?: IdCompany;
  id_company?: IdCompany;
  idCompany?: IdCompany;
  /** Nombre comercial (campo real del API: `name`). */
  name?: string;
  company_name?: string;
  companyName?: string;
  /** Razón social (§Companies rev. 2026-09-25). */
  legal_name?: string;
  legalName?: string;
  /** PK de la empresa en banca; uso interno. */
  org_company_id?: IdCompany | null;
  orgCompanyId?: IdCompany | null;
  /** Handle UUID externo del comercio; uso interno. */
  external_ref?: string;
  externalRef?: string;
  cobra_a?: CobraA;
  cobraA?: CobraA;
  gateway_account_id?: string | null;
  gatewayAccountId?: string | null;
  is_active?: boolean;
  isActive?: boolean;
}

/** Filtros de `GET /api/v1/payments/companies/`. */
export interface PaymentCompanyListParams {
  /**
   * Búsqueda case-insensitive. El backend matchea contra nombre y razón social.
   * Si se omite o está vacío, devuelve todos los comercios del alcance del usuario.
   */
  search?: string;
  /**
   * Filtra por estado activo/inactivo (§Companies rev. 2026-09-25).
   * `undefined` = todos.
   */
  is_active?: boolean;
  /**
   * Filtra por destino de cobro (§Companies rev. 2026-09-25). Valores del contrato:
   * `'cuenta propia' | 'comercio Memphis' | 'SIN CONFIGURAR'`, o cualquier string
   * (nombre de otro comercio). `undefined` = todos.
   */
  cobra_a?: string;
  /**
   * Server-side pagination (§Companies rev. 2026-09-25). Sin `limit` el backend
   * devuelve TODOS: es opt-in. Cliente lo acota a [1, 200].
   */
  limit?: number;
  /**
   * Desplazamiento server-side (§Companies rev. 2026-09-25). Solo se envía si `> 0`.
   */
  offset?: number;
}

/** Campos comunes del alta: destino (al menos uno lo exige el back) y segundo factor. */
interface PaymentCompanyCreateBase {
  /** `merchant_id`: id numérico del comercio en Memphis, 1–36 dígitos. */
  merchant_id?: string;
  /** `gateway_account_id`: UUID de la cuenta propia en la pasarela. */
  gateway_account_id?: string;
  /** Código del authenticator (step-up, desde 2026-10-02). Nunca se guarda. */
  totp_code: string;
}

/**
 * Cuerpo de `POST /api/v1/payments/companies/` (contrato 2026-10-05): exactamente uno de
 * `id_company` o `name`. `id_company` es `number` y no `IdCompany`: esta ruta, a diferencia
 * del resto de payments, NO acepta el UUID.
 */
export type PaymentCompanyCreateRequest =
  | (PaymentCompanyCreateBase & { id_company: number; name?: never })
  | (PaymentCompanyCreateBase & { name: string; id_company?: never });
